import { getEmojiDataFromNative, Picker } from "emoji-mart";
import "emoji-mart/css/emoji-mart.css";
import data from "emoji-mart/data/all.json";
import React from "react";
import { common_format_utils } from "../../../../common/common_format_utils";
import { Constants } from "../../../../common/constants";
import { CustomEmojiData, GuildData } from "../../../../common/sesh_types";

const initial_category_order = [
	"custom",
	"search",
	"recent",
	"smileys",
	"people",
	"nature",
	"foods",
	"activity",
	"places",
	"objects",
	"symbols",
	"custom-Alphabet",
	"flags",
];

const initial_i18n = {
	search: "Search",
	clear: "Clear", // Accessible label on "clear" button
	notfound: "No Emoji Found",
	skintext: "Choose your default skin tone",
	categories: {
		test: "test",
		search: "Search Results",
		recent: "Frequently Used",
		smileys: "Smileys & Emotion",
		people: "People & Body",
		nature: "Animals & Nature",
		foods: "Food & Drink",
		activity: "Activity",
		places: "Travel & Places",
		objects: "Objects",
		symbols: "Symbols",
		flags: "Flags",
		custom: "Custom",
	},
	categorieslabel: "Emoji categories", // Accessible title for the list of categories
	skintones: {
		1: "Default Skin Tone",
		2: "Light Skin Tone",
		3: "Medium-Light Skin Tone",
		4: "Medium Skin Tone",
		5: "Medium-Dark Skin Tone",
		6: "Dark Skin Tone",
	},
};

const FREQUENTLY_USED_EMOJIS = [
	Constants.RSVP_REACTION,
	Constants.MAYBE_REACTION,
	Constants.NO_REACTION,
	"🛡️",
	"⚔️",
	"✝️",
	"👍",
	"👎",
].map((value) => getEmojiDataFromNative(value, "twitter", data).id);

const EMOJIS_TO_EXCLUDE = [];

interface PickerEmoji {
	name: string;
	short_names: string[];
	text: string;
	emoticons: string[];
	keywords: string[];
	imageUrl: string;
	custom?: boolean;
	customCategory?: string;
}

interface ChosenEmoji {
	name: string;
	imageUrl?: string;
}

interface EmojiPickerProp {
	emoji: string;
	updateFunc: Function;
	name?: string;
	disabled?: boolean;
	custom_emojis?: CustomEmojiData[];
	all_guild_data?: GuildData[];
}

interface EmojiPickerState {
	emoji_picker_open: boolean;
	chosen_emoji: ChosenEmoji;
	custom_emojis: PickerEmoji[];
	invalid_emoji: boolean;
	category_order: string[];
	custom_icons: { categories: Record<string, () => void> };
	all_custom_emojis: CustomEmojiData[];
}

function toUni(str) {
	if (str.length < 4) {
		return str.codePointAt(0).toString(16);
	}
	return (
		str.codePointAt(0).toString(16) + "-" + str.codePointAt(2).toString(16)
	);
}

function getCustomEmojiData(
	emoji: CustomEmojiData,
	is_alphabet = false
): PickerEmoji {
	const keywords = ["custom", emoji.name, emoji.id];

	if (is_alphabet) {
		keywords.push(
			"alphabet",
			emoji.name.split("_")[0],
			"letter",
			"letters"
		);
	}

	return {
		name: emoji.name,
		short_names: [emoji.id],
		text: "",
		emoticons: [],
		keywords: keywords,
		imageUrl:
			`https://cdn.discordapp.com/emojis/${emoji.id}.` +
			(emoji.animated ? "gif" : "png"),
		custom: true,
		customCategory: emoji.guild_name,
	};
}

export default class EmojiPickerForm extends React.Component<
	EmojiPickerProp,
	EmojiPickerState
> {
	constructor(props) {
		super(props);

		let category_order = initial_category_order;

		let custom_emojis: PickerEmoji[] = [];

		let start_emoji;
		let guild_categories = [];
		let custom_icons: EmojiPickerState["custom_icons"] = { categories: {} };

		// name: emoji.name,
		// short_names: [emoji.id],
		// text: "",
		// emoticons: [],
		// keywords: ["custom", emoji.name, emoji.id],
		// imageUrl:
		// 	`https://cdn.discordapp.com/emojis/${emoji.id}.` +
		// 	(emoji.animated ? "gif" : "png"),
		// custom: true,
		// customCategory: emoji.guild_name,

		const all_custom_emojis = [
			...Constants.SESH_CUSTOM_EMOJIS.ALPHABET,
			...(this.props.custom_emojis ?? []),
		];

		custom_icons.categories["custom-Alphabet"] = () => (
			// <img src={alphabet_emojis[0].imageUrl} />
			<div>A</div>
		);

		if (all_custom_emojis && all_custom_emojis.length > 0) {
			for (let emoji of all_custom_emojis) {
				const is_alphabet =
					emoji.guild_id === Constants.SESH_EMOJI_FARM_GUILD_ID &&
					emoji.guild_name === "Alphabet";

				let current_emoji_data = getCustomEmojiData(emoji, is_alphabet);
				let current_emoji_full_name = emoji.id;

				if (current_emoji_full_name === this.props.emoji) {
					start_emoji = {
						name: current_emoji_full_name,
						imageUrl: current_emoji_data.imageUrl,
					};
				}

				custom_emojis.push(current_emoji_data);

				if (is_alphabet) {
					continue;
				}

				let category_name = "custom-" + emoji.guild_name;

				if (!guild_categories.includes(category_name)) {
					guild_categories.push(category_name);

					let correct_guild = this.props.all_guild_data.find(
						(guild_data) => guild_data.id === emoji.guild_id
					);

					if (correct_guild && correct_guild.icon) {
						custom_icons.categories[category_name] = () => (
							<img
								src={common_format_utils.get_guild_icon_url(
									correct_guild
								)}
							/>
						);
					}
				}
			}
		}

		category_order = guild_categories.concat(category_order);

		let invalid_emoji = false;
		if (!start_emoji && this.props.emoji) {
			invalid_emoji = true;
			for (let i = 0; i < this.props.emoji.length; i++) {
				let char = this.props.emoji.charAt(i);
				if (!(char >= "0" && char <= "9")) {
					invalid_emoji = false;
				}
			}

			start_emoji = { name: this.props.emoji };
		}

		this.state = {
			emoji_picker_open: false,
			chosen_emoji: start_emoji,
			custom_emojis: custom_emojis,
			invalid_emoji: invalid_emoji,
			category_order: category_order,
			custom_icons: custom_icons,
			all_custom_emojis,
		};

		this.onButtonPressed = this.onButtonPressed.bind(this);
		this.onEmojiClick = this.onEmojiClick.bind(this);
	}

	onButtonPressed(event, is_child?: boolean) {
		event.preventDefault();
		event.stopPropagation();
		if (is_child) {
			return;
		}

		this.setState({ emoji_picker_open: !this.state.emoji_picker_open });
	}

	onEmojiClick(emoji, event) {
		event.preventDefault();
		event.stopPropagation();

		let chosen_emoji;
		let custom_emoji_data: CustomEmojiData;
		if (emoji.custom) {
			let emoji_data = this.state.all_custom_emojis.find(
				(val) => val.id === emoji.short_names[0]
			);

			chosen_emoji = {
				name: emoji.short_names[0],
				imageUrl: emoji.imageUrl,
			};

			custom_emoji_data = emoji_data;
		} else {
			chosen_emoji = { name: emoji.native };
		}

		this.setState({ chosen_emoji: chosen_emoji });
		this.props.updateFunc({
			emoji: chosen_emoji.name,
			custom_emoji_data: custom_emoji_data,
		});
	}

	emojiFilter(emoji) {
		if (emoji.custom) {
			return true;
		}

		let uni_to_exclude = EMOJIS_TO_EXCLUDE.map((value) => toUni(value));
		return !uni_to_exclude.includes(emoji.unified.toLowerCase());
	}

	displayEmojiButton(emoji: ChosenEmoji) {
		let inner;
		let className;

		if (this.state.invalid_emoji) {
			className = "emoji-picker-button";
			inner = "⚠️";
		} else if (emoji.imageUrl) {
			className = "emoji-picker-image";
			inner = (
				<img
					style={{ maxWidth: "50%", maxHeight: "50%" }}
					src={emoji.imageUrl}
				></img>
			);
		} else {
			className = "emoji-picker-button";
			inner = emoji.name;
		}

		return (
			<button
				className={className}
				onClick={this.onButtonPressed}
				disabled={Boolean(this.props.disabled)}
			>
				{inner}
			</button>
		);
	}

	render() {
		return (
			<div className="control">
				Emoji{" "}
				{this.state.invalid_emoji && (
					<p style={{ color: "red" }}>
						<b>not found</b>
					</p>
				)}
				<div>{this.displayEmojiButton(this.state.chosen_emoji)}</div>
				{this.state.emoji_picker_open && (
					<div
						className="overlay"
						onClick={(event) => this.onButtonPressed(event, false)}
					>
						<div
							className="emoji_picker_overlay"
							onClick={(event) =>
								this.onButtonPressed(event, true)
							}
						>
							<Picker
								title="sesh"
								custom={this.state.custom_emojis}
								set={"twitter"}
								recent={FREQUENTLY_USED_EMOJIS}
								onClick={this.onEmojiClick}
								include={this.state.category_order}
								theme={"dark"}
								emojisToShowFilter={this.emojiFilter}
								autoFocus={true}
								showSkinTones={false}
								disabled={Boolean(this.props.disabled)}
								icons={this.state.custom_icons}
							></Picker>
						</div>
					</div>
				)}
			</div>
		);
	}
}
